import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useSyncExternalStore } from 'react';

/**
 * Reads a single item by id out of whatever `[domainKey, 'paginated', ...]`
 * TanStack Query caches already hold, without issuing a new request — some
 * ancestor already fetched the list this id lives in, e.g. via
 * `queryOptionsGetClientsPaginated` / `queryOptionsGetTitlesPaginated`. The
 * lookup components that use this (`LookupClientName`, `LookupTitleName`,
 * `DisplayClientName`) are rendered in too many different places, under too
 * many different pagination/filter params, to know which single parent query
 * to `select` from — so instead of firing a per-id fetch (a real N+1 the old
 * Redux-selector version never had), this scans every cached paginated query
 * for the domain.
 *
 * Subscribed via `useSyncExternalStore` (not a one-shot `useMemo`) so a value
 * that lands in the cache after first render — a slow fetch, or one kicked
 * off by a sibling — is picked up instead of staying `null` forever.
 */
export const useCachedPaginatedItem = <TItem extends { _id: string }>(
  domainKey: string,
  id?: null | string,
): TItem | null => {
  const queryClient = useQueryClient();
  // Holds the last value `getSnapshot` returned, so an unrelated cache event
  // for the same domain (one that doesn't actually change this item) can
  // return the *same* reference instead of a fresh one — `useSyncExternalStore`
  // treats any new reference as a change worth re-rendering for.
  const lastResultRef = useRef<TItem | null>(null);

  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      queryClient.getQueryCache().subscribe((event) => {
        // Every `QueryCacheNotifyEvent` variant carries the `query` it's
        // about; skip events for other domains so an unrelated query
        // updating elsewhere in the cache doesn't force this hook to
        // re-scan and re-render.
        if (event.query.queryKey[0] !== domainKey) return;

        onStoreChange();
      }),
    [queryClient, domainKey],
  );

  const getSnapshot = useCallback((): TItem | null => {
    if (!id) {
      lastResultRef.current = null;

      return null;
    }

    const queries = queryClient
      .getQueryCache()
      .findAll({ queryKey: [domainKey, 'paginated'] });

    // Multiple paginated queries for the same domain commonly coexist in the
    // cache at once (a page's own list, a card's warm-up fetch, a search,
    // etc). An inactive one can hold a stale copy of this item after an
    // update invalidates only the actively-observed queries, so prefer
    // whichever match was written to most recently rather than the first one
    // cache-registration order happens to surface.
    let bestItem: TItem | null = null;
    let bestUpdatedAt = -Infinity;

    for (const query of queries) {
      const data = query.state.data as { items?: TItem[] } | undefined;
      const found = data?.items?.find((item) => item._id === id);

      if (found && query.state.dataUpdatedAt >= bestUpdatedAt) {
        bestItem = found;
        bestUpdatedAt = query.state.dataUpdatedAt;
      }
    }

    if (itemsEqual(bestItem, lastResultRef.current)) {
      return lastResultRef.current;
    }

    lastResultRef.current = bestItem;

    return bestItem;
  }, [domainKey, id, queryClient]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};

const itemsEqual = <TItem extends { _id: string }>(
  a: TItem | null,
  b: TItem | null,
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;

  return JSON.stringify(a) === JSON.stringify(b);
};
